import { Link, NavLink, Outlet } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { BookmarkSimple, SignOut, ShieldCheck, ArrowUpRight, List as MenuIcon, X, Gear } from "@phosphor-icons/react";
import Concierge from "@/components/Concierge";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import NativeBridge from "@/components/NativeBridge";
import ThemeToggle from "@/components/ThemeToggle";

const navKeys = [
  { to: "/conferences", key: "nav.conferences", testid: "nav-conferences" },
  { to: "/cfp", key: "nav.cfp", testid: "nav-cfp" },
  { to: "/speakers", key: "nav.speakers", testid: "nav-speakers" },
  { to: "/papers", key: "nav.papers", testid: "nav-papers" },
  { to: "/map", key: "nav.atlas", testid: "nav-map" },
];

function Logo({ size = "md" }) {
  const cls = size === "lg" ? "text-2xl" : "text-base";
  return (
    <span className={`logo ${cls}`} aria-label="Symposia" style={{ direction: "ltr" }}>
      <span>SYMPOSI</span>
      <span className="logo-dot" />
      <span style={{marginLeft:'.05em'}}>A</span>
    </span>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [drawer, setDrawer] = useState(false);
  const year = new Date().getFullYear();
  const isRtl = i18n.dir() === "rtl";

  function closeDrawer() { setDrawer(false); }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-zinc-950 text-zinc-950 dark:text-zinc-100">
      <header className="nav-blur sticky top-0 z-50 border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-950/80">
        <div className="px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <Logo />
            <span className="hidden lg:inline overline">/ {t("nav.tagline")}</span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {navKeys.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `text-sm font-medium link-ul tracking-tight ${isActive ? "text-zinc-950" : "text-zinc-500 hover:text-zinc-950"}`
                }
              >
                {t(n.key)}
              </NavLink>
            ))}
            <Link
              to="/speakers/me"
              data-testid="nav-become-speaker"
              className="text-xs font-mono uppercase tracking-[0.14em] px-3 py-1.5 border border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white transition-colors whitespace-nowrap"
            >
              {t("nav.becomeSpeaker") || "Become a speaker"} →
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LanguageSwitcher />
            {user && user.role ? (
              <>
                <Link to="/profile" data-testid="nav-profile" className="hidden sm:inline-flex btn-ghost items-center gap-1.5">
                  <span>{t("nav.profile")}</span>
                </Link>
                <Link to="/bookmarks" data-testid="nav-bookmarks" className="hidden lg:inline-flex btn-ghost items-center gap-1.5">
                  <BookmarkSimple size={16} weight="regular" />
                  <span>{t("nav.saved")}</span>
                </Link>
                <Link to="/organize" data-testid="nav-organize" className="hidden lg:inline-flex btn-ghost items-center gap-1.5">
                  <span>{t("nav.organize")}</span>
                </Link>
                <Link to="/settings" data-testid="nav-settings" className="hidden lg:inline-flex btn-ghost items-center gap-1.5">
                  <Gear size={16} />
                  <span>{t("nav.settings")}</span>
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin" className="hidden sm:inline-flex btn-ghost items-center gap-1.5">
                    <ShieldCheck size={16} />
                    <span>{t("nav.admin")}</span>
                  </Link>
                )}
                <div className="hidden md:flex items-center gap-2 pl-3 ml-1 border-l border-zinc-200 rtl:border-l-0 rtl:border-r rtl:pl-0 rtl:pr-3 rtl:ml-0 rtl:mr-1">
                  <div className="w-8 h-8 rounded-full bg-zinc-950 text-white flex items-center justify-center text-xs font-semibold uppercase">
                    {user.email[0]}
                  </div>
                  <button onClick={logout} data-testid="nav-logout" className="text-xs text-zinc-500 hover:text-zinc-950 flex items-center gap-1">
                    <SignOut size={14} /> {t("nav.logout")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex btn-ghost">{t("nav.signIn")}</Link>
                <Link to="/register" data-testid="nav-register" className="hidden sm:inline-flex btn-primary">
                  {t("nav.openAccount")} <ArrowUpRight size={14} data-arrow="forward" />
                </Link>
              </>
            )}
            <button onClick={() => setDrawer(true)} data-testid="nav-menu-btn" aria-label={t("nav.menu")} className="md:hidden p-2 hover:bg-zinc-100 -mr-2 rtl:-mr-0 rtl:-ml-2">
              <MenuIcon size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-[70] md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/30" onClick={closeDrawer} />
          <div className={`absolute top-0 ${isRtl ? "left-0 border-r" : "right-0 border-l"} h-full w-[85%] max-w-sm bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 flex flex-col`}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-800">
              <Logo />
              <button onClick={closeDrawer} data-testid="mobile-drawer-close" aria-label={t("common.close") || "Close"} className="p-3 -m-3 hover:bg-zinc-100 dark:hover:bg-zinc-800"><X size={20} /></button>
            </div>
            <nav className="flex-1 px-5 py-6 space-y-1">
              {navKeys.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={closeDrawer} data-testid={`drawer-${n.testid}`} className={({ isActive }) => `block px-3 py-3 text-lg font-display font-medium tracking-tight ${isActive ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950" : "hover:bg-zinc-100 dark:hover:bg-zinc-800"}`}>
                  {t(n.key)}
                </NavLink>
              ))}
              <NavLink to="/speakers/me" onClick={closeDrawer} data-testid="drawer-become-speaker" className="block px-3 py-3 text-base border border-blue-700 text-blue-700 mt-3">
                {t("nav.becomeSpeaker") || "Become a speaker"} →
              </NavLink>
              <div className="border-t border-zinc-200 dark:border-zinc-800 mt-4 pt-4 space-y-1">
                {user && user.role ? (
                  <>
                    <NavLink to="/profile" onClick={closeDrawer} data-testid="drawer-profile" className="block px-3 py-3 text-base hover:bg-zinc-100 dark:hover:bg-zinc-800">{t("nav.profile")}</NavLink>
                    <NavLink to="/bookmarks" onClick={closeDrawer} data-testid="drawer-bookmarks" className="block px-3 py-3 text-base hover:bg-zinc-100 dark:hover:bg-zinc-800">{t("nav.saved")}</NavLink>
                    <NavLink to="/organize" onClick={closeDrawer} data-testid="drawer-organize" className="block px-3 py-3 text-base hover:bg-zinc-100 dark:hover:bg-zinc-800">{t("nav.organize")}</NavLink>
                    <NavLink to="/settings" onClick={closeDrawer} data-testid="drawer-settings" className="block px-3 py-3 text-base hover:bg-zinc-100 dark:hover:bg-zinc-800">{t("nav.settings")}</NavLink>
                    {user.role === "admin" && <NavLink to="/admin" onClick={closeDrawer} data-testid="drawer-admin" className="block px-3 py-3 text-base hover:bg-zinc-100 dark:hover:bg-zinc-800">{t("nav.admin")}</NavLink>}
                    <button onClick={() => { logout(); closeDrawer(); }} data-testid="drawer-logout" className="block w-full text-start px-3 py-3 text-base hover:bg-zinc-100 dark:hover:bg-zinc-800">{t("nav.logout")}</button>
                  </>
                ) : (
                  <>
                    <NavLink to="/login" onClick={closeDrawer} data-testid="drawer-signin" className="block px-3 py-3 text-base hover:bg-zinc-100 dark:hover:bg-zinc-800">{t("nav.signIn")}</NavLink>
                    <NavLink to="/register" onClick={closeDrawer} data-testid="drawer-register" className="block px-3 py-3 text-base bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950">{t("nav.openAccount")}</NavLink>
                  </>
                )}
              </div>
            </nav>
            <div className="px-5 py-4 border-t border-zinc-200 dark:border-zinc-800 text-xs font-mono text-zinc-500 dark:text-zinc-400">© {t("footer.symposia")}</div>
          </div>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-zinc-200 dark:border-zinc-800 bg-[#FAFAFA] dark:bg-zinc-900">
        <div className="px-6 md:px-10 lg:px-16 py-14">
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <Logo size="lg" />
              <p className="mt-4 text-sm text-zinc-500 max-w-sm leading-relaxed">
                {t("footer.tagline")}
              </p>
            </div>
            <div className="md:col-span-2">
              <div className="overline mb-3">{t("footer.browse")}</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/conferences" className="hover:underline">{t("nav.conferences")}</Link></li>
                <li><Link to="/cfp" className="hover:underline">{t("nav.cfp")}</Link></li>
                <li><Link to="/speakers" className="hover:underline">{t("nav.speakers")}</Link></li>
                <li><Link to="/papers" className="hover:underline">{t("nav.papers")}</Link></li>
                <li><Link to="/map" className="hover:underline">{t("nav.atlas")}</Link></li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="overline mb-3">{t("footer.symposia")}</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:underline">{t("footer.about")}</Link></li>
                <li><Link to="/developers" className="hover:underline">{t("footer.developers")}</Link></li>
                <li><Link to="/sponsor" className="hover:underline">{t("footer.sponsor")}</Link></li>
                <li><Link to="/speakers/me" className="hover:underline">{t("footer.becomeSpeaker")}</Link></li>
                <li><Link to="/terms" className="hover:underline">{t("footer.terms")}</Link></li>
                <li><Link to="/privacy" className="hover:underline">{t("footer.privacy")}</Link></li>
                <li><a href={`${process.env.REACT_APP_BACKEND_URL}/api/feed.xml`} target="_blank" rel="noreferrer" className="hover:underline">{t("footer.rss")}</a></li>
              </ul>
            </div>
            <div className="md:col-span-4">
              <div className="overline mb-3">{t("footer.edition", { year })}</div>
              <div className="font-mono text-xs text-zinc-500 leading-relaxed">
                {t("footer.footerLine")}<br />
                <a href="mailto:hello@conferencely.org" className="underline">hello@conferencely.org</a><br />
                {t("footer.rights", { year })}
              </div>
            </div>
          </div>
        </div>
      </footer>

      <Concierge />
      <NativeBridge />
    </div>
  );
}
